import { forwardRef } from 'react';

import { cx } from '../../lib/cx';
import { Spinner } from '../spinner';
import type { ButtonProps, ButtonSize, ButtonVariant } from './types';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500',
  secondary:
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-300',
  danger: 'bg-danger-600 text-white hover:bg-danger-700 focus-visible:ring-danger-600',
  ghost: 'text-gray-600 hover:bg-gray-100 focus-visible:ring-gray-300',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'control-touch px-3 py-1.5 text-sm',
  md: 'control-touch px-4 py-2 text-sm',
  lg: 'control-touch px-5 py-2.5 text-base',
};

/**
 * The button. One `primary` per screen; `loading` blocks double submits and announces itself.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    disabled,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-control font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  );
});
